import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { createPlanParto, getPlanPartoDetalle, getPlanPartoHistorico, updatePlanParto } from "../../api/planParto";
import { calculateAge } from "../../utils/ageCalculator";
import { calculateGestationData } from "../../utils/gestationCalculator";

export default function usePlanParto() {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [saving, setSaving] = useState(false);

  const [idPlanParto, setIdPlanParto] = useState(null);
  const [plan, setPlan] = useState(getEmptyPlan);
  const [epsTramite, setEpsTramite] = useState(getEmptyEps);

  const canSave = useMemo(() => !!selectedPatient?.Unic_ID && !!selectedPatient?.autoid, [selectedPatient]);

  useEffect(() => {
    if (!plan?.fum) return;
    const gestData = calculateGestationData(plan.fum);
    if (!gestData || typeof gestData.semanas_gestacion === "undefined") return;

    setPlan((prev) => {
      const nextWeeks = gestData.semanas_gestacion;
      const nextFpp = gestData.fecha_probable_parto;

      const sameWeeks = Number(prev.semanas_gestacion) === Number(nextWeeks);
      const sameFpp = String(prev.fpp || "") === String(nextFpp || "");
      if (sameWeeks && sameFpp) return prev;

      return {
        ...prev,
        semanas_gestacion: nextWeeks,
        fpp: nextFpp,
      };
    });
  }, [plan?.fum]);

  const resetForm = useCallback((keepPatient = true) => {
    setIdPlanParto(null);
    setPlan((prev) => ({
      ...getEmptyPlan(),
      Unic_ID: keepPatient ? (prev.Unic_ID || "") : "",
      autoid: keepPatient ? (prev.autoid || "") : "",
    }));
    setEpsTramite(getEmptyEps());
    if (!keepPatient) {
      setSelectedPatient(null);
      setHistorico([]);
    }
  }, []);

  const loadHistorico = useCallback(async (unicId) => {
    try {
      setLoadingHistorico(true);
      const payload = await getPlanPartoHistorico(unicId);
      if (!payload.success) {
        // toast.error(payload.message || "No se pudo cargar el histórico");
        setHistorico([]);
        return;
      }
      setHistorico(payload.data || []);
    } catch (e) {
      // toast.error(e.response?.data?.message || "Error al cargar histórico");
      setHistorico([]);
    } finally {
      setLoadingHistorico(false);
    }
  }, []);

  const loadDetalle = useCallback(async (id) => {
    try {
      setLoadingDetalle(true);
      const payload = await getPlanPartoDetalle(id);
      if (!payload.success) {
        toast.error(payload.message || "No se pudo cargar el registro");
        return;
      }

      const planDb = payload.data?.plan || {};
      const epsDb = payload.data?.eps_tramite || {};

      setIdPlanParto(planDb.id_plan_parto || id);
      setPlan((prev) => ({
        ...prev,
        ...planDb,
        Unic_ID: prev.Unic_ID,
        autoid: prev.autoid,
      }));
      setEpsTramite((prev) => ({ ...prev, ...epsDb }));
    } catch (e) {
      toast.error(e.response?.data?.message || "Error al cargar detalle");
    } finally {
      setLoadingDetalle(false);
    }
  }, []);

  const handleSelectPatient = useCallback(
    async (p) => {
      if (!p?.Unic_ID || !p?.autoid) {
        toast.error("El paciente seleccionado no trae Unic_ID/autoid");
        return;
      }

      const raw = p?.raw || {};
      const edad = raw?.Fecha_Nto ? calculateAge(raw.Fecha_Nto).years : "";

      setSelectedPatient(p);
      setPlan({
        ...getEmptyPlan(),
        Unic_ID: p.Unic_ID,
        autoid: p.autoid,
        direccion: raw?.DirPaci || "",
        contacto: raw?.TelPaci || "",
        eps: raw?.Entidad || "",
        edad,
      });
      setEpsTramite(getEmptyEps());
      setIdPlanParto(null);
      await loadHistorico(p.Unic_ID);
    },
    [loadHistorico]
  );

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!canSave) {
        toast.warning("Debe seleccionar un paciente");
        return;
      }

      const payload = {
        plan: normalizePayload({
          ...plan,
          Unic_ID: selectedPatient.Unic_ID,
          autoid: selectedPatient.autoid,
          edad: toNullIfEmpty(plan.edad),
          semanas_gestacion: toNullIfEmpty(plan.semanas_gestacion),
          peso_kg: toNullIfEmpty(plan.peso_kg),
          num_controles: toNullIfEmpty(plan.num_controles),
          prioridad_dias: toNullIfEmpty(plan.prioridad_dias),
        }),
        ...(hasEpsTramiteData(epsTramite)
          ? {
              eps_tramite: normalizePayload({
                ...epsTramite,
                dias_transcurridos: toNullIfEmpty(epsTramite.dias_transcurridos),
              }),
            }
          : {}),
      };

      try {
        setSaving(true);
        if (idPlanParto) {
          const res = await updatePlanParto({ id_plan_parto: idPlanParto, ...payload });
          if (!res.success) {
            toast.error(res.message || "Error actualizando");
            return;
          }
          toast.success(res.message || "Actualizado");
          await loadHistorico(selectedPatient.Unic_ID);
        } else {
          const res = await createPlanParto(payload);
          if (!res.success) {
            toast.error(res.message || "Error creando");
            return;
          }
          const newId = res.data?.id_plan_parto;
          toast.success(res.message || "Creado");
          await loadHistorico(selectedPatient.Unic_ID);
          if (newId) {
            await loadDetalle(newId);
          }
        }
      } catch (err) {
        toast.error(err.response?.data?.message || err.message || "Error guardando");
      } finally {
        setSaving(false);
      }
    },
    [canSave, epsTramite, idPlanParto, loadDetalle, loadHistorico, plan, selectedPatient]
  );

  return {
    canSave,
    epsTramite,
    handleSelectPatient,
    historico,
    idPlanParto,
    loadDetalle,
    loadingDetalle,
    loadingHistorico,
    plan,
    resetForm,
    saving,
    selectedPatient,
    setEpsTramite,
    setPlan,
    submit,
  };
}

const toNullIfEmpty = (v) => (v === "" ? null : v);

const normalizePayload = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v === "") out[k] = null;
    else out[k] = v;
  });
  return out;
};

const hasEpsTramiteData = (eps) => {
  if (!eps) return false;
  const textFields = ["eps_tramite", "fecha_socializacion", "profesional_eps", "fecha_respuesta", "dias_transcurridos"];
  const hasText = textFields.some((k) => {
    const v = eps[k];
    if (v === null || v === undefined) return false;
    if (typeof v === "string") return v.trim() !== "";
    return true;
  });
  const hasAnyAnexo = !!(eps.anexo_plan_parto_discutido || eps.anexo_resumen_hc || eps.anexo_carnet_clap);
  return hasText || hasAnyAnexo;
};

const getEmptyPlan = () => ({
  Unic_ID: "",
  autoid: "",
  direccion: "",
  contacto: "",
  edad: "",
  eps: "",
  fecha_diligenciamiento: getTodayISODate(),
  semanas_gestacion: "",
  fum: "",
  fpp: "",
  grupo_sanguineo: "",
  rh: "",
  peso_kg: "",
  alergias: "",
  fecha_ingreso_cpn: "",
  num_controles: "",
  acompanante: "",
  contacto_acompanante: "",
  presentacion: "",
  posicion: "",
  situacion: "",
  descenso: "",
  asesoria_recibida: false,
  metodo_elegido: "",
  prestador_parto_normal: "",
  prestador_urgencias_cesarea: "",
  apoyo_hogar: null,
  nombre_apoyo_hogar: "",
  solicitud_eps_traslado_aro: false,
  solicitud_eps_traslado_cesarea: false,
  solicitud_eps_estancia_previa_rural: false,
  solicitud_eps_riesgo_zona: false,
  solicitud_eps_albergue: false,
  solicitud_eps_acompanante_logistico: false,
  interconsulta_especialidad: "",
  prioridad_dias: "",
  medico_tradicional: null,
  partera: null,
  plan_seguridad_red_urgencias: false,
  plan_seguridad_sistema_referencia: false,
  plan_seguridad_transporte_comunitario: false,
  plan_seguridad_identificacion_prestador: false,
  plan_seguridad_situaciones_activacion: false,
  impresion_diagnostica: "",
  causales_aro: "",
  via_parto_sugerida: "",
  signos_alarma_neurologicos: false,
  signos_alarma_edema: false,
  signos_alarma_sangrado: false,
  signos_alarma_dolor_abdominal: false,
  signos_alarma_movimientos: false,
  signos_alarma_contracciones: false,
  kit_ingreso_hc_completa: false,
  kit_ingreso_documentos: false,
  kit_ingreso_kit_rn: false,
  kit_ingreso_kit_materna: false,
  kit_ingreso_autorizaciones: false,
  pref_ambiente_luz_tenue: false,
  pref_ambiente_musica: false,
  pref_ambiente_privacidad: false,
  pref_acompanamiento_continuo: false,
  personas_acompanantes_sala: "",
  libertad_movimiento: false,
  pref_intervenciones_consentimiento: false,
  pref_intervenciones_tactos: false,
  pref_intervenciones_episiotomia: false,
  pref_intervenciones_oxitocina: false,
  pref_intervenciones_rasurado: false,
  pref_intervenciones_kristeller: false,
  pref_analgesia: "",
  pref_rn_contacto_piel: false,
  pref_rn_lactancia_precoz: false,
  pref_rn_pinzamiento_retrasado: false,
  pref_rn_corte_acompanante: false,
  pref_rn_entrega_placenta: false,
  pref_cesarea_acompanante_quirofano: false,
  pref_cesarea_contacto_cesarea: false,
  pref_postparto_alojamiento_conjunto: false,
  pref_postparto_vigilancia_signos: false,
});

const getTodayISODate = () => new Date().toISOString().split("T")[0];

const getEmptyEps = () => ({
  eps_tramite: "",
  fecha_socializacion: "",
  profesional_eps: "",
  fecha_respuesta: "",
  dias_transcurridos: "",
  anexo_plan_parto_discutido: false,
  anexo_resumen_hc: false,
  anexo_carnet_clap: false,
});
